import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import fetchApi from '../fetchApi';
import { APP_MODULE } from 'constants/module.constant';
import { clearTenantQueryCache } from 'utils/tenantQueryCache';

export const ADMIN_TENANT_QUERY_KEYS = {
  list: (module) => ['admin', 'tenants', { module }],
  modules: (tenantId) => ['admin', 'tenants', tenantId, 'modules'],
};

/**
 * List tenants the administrator can pick from, scoped to the current
 * frontend module via the `?module=` query string.
 */
export const useAdminTenantsQuery = (options = {}) => {
  const module = APP_MODULE.toLowerCase();

  return useQuery({
    queryKey: ADMIN_TENANT_QUERY_KEYS.list(module),
    queryFn: () => fetchApi(`admin/tenants?module=${module}`).get(),
    staleTime: 5 * 60 * 1000,
    select: (response) => response?.data?.tenants ?? [],
    ...options,
  });
};

/**
 * Persist the selected tenant on the backend (audit/log only — the
 * stateless context comes from the X-Admin-Tenant-ID header).
 */
export const useAdminSelectTenantMutation = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantId) =>
      fetchApi('admin/select-tenant').post({ tenant_id: tenantId }),
    onSuccess: (data, tenantId) => {
      // Call the consumer callback first so selectTenant() writes the new
      // tenant ID to localStorage *before* we clear the cache. If we cleared
      // first, any mounted queries would auto-refetch without the correct
      // X-Admin-Tenant-ID header and populate the cache with wrong-tenant data.
      options.onSuccess?.(data, tenantId);
      // Wipe tenant-scoped cache (AdminTenantProvider also clears on tenant
      // change; this runs synchronously here so mounted queries cannot refetch
      // with a stale header before the provider effect fires).
      clearTenantQueryCache(queryClient);
    },
    onError: (error) => {
      options.onError?.(error);
    },
    ...options,
  });
};
